import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/pages/Checkout";

function Row({ k, v, testId }) {
  return (
    <div className="flex justify-between gap-4 border-b border-dashed border-border py-2 text-sm">
      <span className="text-muted-foreground">{k}</span><span className="text-right font-medium" data-testid={testId}>{v}</span>
    </div>
  );
}

export default function ReceiptPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/payments/${id}/receipt`).then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, [id]);

  if (err) return <div className="p-10 text-center text-sm" data-testid="receipt-error">{err} <Link to="/my-courses" className="underline">Back</Link></div>;
  if (!data) return <div className="mx-auto max-w-2xl p-10"><Skeleton className="h-96" /></div>;
  const { payment: p, organization: o } = data;
  const addr = [o.address_line1, o.address_line2, o.city, o.state, o.pincode, o.country].filter(Boolean).join(", ");

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 print:py-0">
      <div className="mb-4 flex justify-between print:hidden">
        <Button asChild variant="ghost" size="sm"><Link to="/my-courses"><ArrowLeft className="mr-1 h-4 w-4" /> My Courses</Link></Button>
        <Button size="sm" onClick={() => window.print()} data-testid="receipt-print-btn"><Printer className="mr-1.5 h-4 w-4" /> Print / Save as PDF</Button>
      </div>
      <div className="rounded-2xl border border-border bg-white p-8" data-testid="receipt">
        <div className="flex items-start justify-between gap-4 border-b border-border pb-5">
          <div className="flex items-center gap-3">
            {o.logo_url && <img src={o.logo_url} alt="" className="h-12 w-12 rounded object-contain" />}
            <div>
              <div className="font-display text-xl font-black">{o.academy_name || o.site_name}</div>
              {addr && <div className="text-xs text-muted-foreground">{addr}</div>}
              <div className="text-xs text-muted-foreground">{[o.email, o.phone_primary].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Payment Receipt</div>
            <div className="font-mono text-sm" data-testid="receipt-number">{p.receipt_no}</div>
          </div>
        </div>
        <div className="mt-5">
          <Row k="Student" v={`${p.user_name || ""} (${p.user_email || ""})`} />
          <Row k="Course" v={p.course_name} testId="receipt-course" />
          <Row k="Amount paid" v={inr(p.amount)} testId="receipt-amount" />
          <Row k="Status" v="PAID" testId="receipt-status" />
          <Row k="Payment method" v={(p.method || "—").toUpperCase()} />
          <Row k="Payment ID" v={p.payment_id} />
          <Row k="Order ID" v={p.order_id} />
          <Row k="Transaction date" v={new Date(p.paid_at).toLocaleString("en-IN")} />
        </div>
        <p className="mt-6 text-xs text-muted-foreground">This is a computer-generated receipt and does not require a signature.{p.mock ? " (Test transaction — no money was charged.)" : ""}</p>
      </div>
    </div>
  );
}
